import type { Ref } from 'vue';

import { onUnmounted, ref } from 'vue';

export type SlideDirection = 'next' | 'prev';

export interface DirectionalSlideClassMap {
  slideOutNext: string;
  slideOutPrev: string;
  slideInNext: string;
  slideInPrev: string;
}

export interface UseDirectionalSlideAnimationOptions {
  classMap: DirectionalSlideClassMap;
  slideOutDurationMs: number;
  slideInDurationMs: number;
}

export interface UseDirectionalSlideAnimationReturn {
  activeAnimationClass: Ref<string | null>;
  isSlideHidden: Ref<boolean>;
  startNavigation: (direction: SlideDirection) => void;
  finishNavigation: () => void;
  cancelNavigation: () => void;
}

export default function useDirectionalSlideAnimation(
  options: UseDirectionalSlideAnimationOptions,
): UseDirectionalSlideAnimationReturn {
  const activeAnimationClass = ref<string | null>(null);
  const isSlideHidden = ref(false);
  const pendingDirection = ref<SlideDirection | null>(null);
  let animationTimeoutId: number | null = null;

  function clearTimeoutIfNeeded(): void {
    if (animationTimeoutId === null) {
      return;
    }

    window.clearTimeout(animationTimeoutId);
    animationTimeoutId = null;
  }

  function runAnimation(
    animationClass: string,
    durationMs: number,
    keepFinalState = false,
  ): void {
    clearTimeoutIfNeeded();

    activeAnimationClass.value = null;

    requestAnimationFrame(() => {
      activeAnimationClass.value = animationClass;

      if (keepFinalState) {
        return;
      }

      animationTimeoutId = window.setTimeout(() => {
        activeAnimationClass.value = null;
        animationTimeoutId = null;
      }, durationMs);
    });
  }

  function startNavigation(direction: SlideDirection): void {
    pendingDirection.value = direction;
    isSlideHidden.value = false;

    runAnimation(
      direction === 'next'
        ? options.classMap.slideOutNext
        : options.classMap.slideOutPrev,
      options.slideOutDurationMs,
      true,
    );
  }

  function finishNavigation(): void {
    if (!pendingDirection.value) {
      return;
    }

    const direction = pendingDirection.value;
    pendingDirection.value = null;

    isSlideHidden.value = true;

    runAnimation(
      direction === 'next'
        ? options.classMap.slideInNext
        : options.classMap.slideInPrev,
      options.slideInDurationMs,
    );

    requestAnimationFrame(() => {
      isSlideHidden.value = false;
    });
  }

  function cancelNavigation(): void {
    pendingDirection.value = null;
    clearTimeoutIfNeeded();
    activeAnimationClass.value = null;
    isSlideHidden.value = false;
  }

  onUnmounted(() => {
    clearTimeoutIfNeeded();
  });

  return {
    activeAnimationClass,
    isSlideHidden,
    startNavigation,
    finishNavigation,
    cancelNavigation,
  };
}
